import { AMOUNT_FORMAT_TYPES, AMOUNT_TYPES } from '@ezugi/constants';

import getSymbolByCurrencyCode from '../core/symbol';

const addAmountToFixed = (amount, decimal) => {
  // make sure decimal value is valid
  if (decimal === undefined || isNaN(decimal)) decimal = 2;
  // execute to fixed command to trim decimal
  amount = Number(amount).toFixed(decimal);
  // make sure if decimal is 00, trim it out
  if (Number(amount) % 1 === 0) amount = Number(amount).toFixed(0);
  return amount;
};

// add display amount separator ('1,000', '23,000,123')
const addAmountSeparator = (amount, separator) => {
  const returnArr = [];
  const splitValue = String(amount).split('.'); // split value to separate value from decimal value
  const value = splitValue[0];
  const decimalValue = splitValue.length > 1 ? `.${splitValue[1]}` : '';
  const count = 3; // every 3 letters add separator
  let totalCount = 0; // keep count of chars added so far

  // loop value chars from end to start (to add separators correctly)
  for (let i = value.length - 1; i >= 0; i--) {
    // add current looped char
    returnArr.push(value.charAt(i));
    totalCount++;
    // if passed 3 letters from the end, add separator, also make sure its not the first char
    if (totalCount % count === 0 && i - 1 >= 0) returnArr.push(separator);
  }
  // since we added to arr from end, we need to reverse the array and convert it back to string, re-add decimal
  return returnArr.reverse().join('') + decimalValue;
};

export function formatAmount(
  amount,
  amountType,
  formatConfig,
  currencyCode,
  showCurrencySymbol, // used to override the configuration attribute
  showCurrencyCode // used to override the configuration attribute
) {
  // make sure received value is number
  let finalAmount = Number(amount);

  // Make sure handling negative values correctly by treating them as non negative and multiplying by -1 at the end
  const isNegative = finalAmount < 0;
  let returnValue = '0';

  finalAmount = Math.abs(amount);

  // Set format type and currency display according to format type received
  let formatType = AMOUNT_TYPES.BET;
  let boShowCurrency = false;

  switch (amountType) {
  case AMOUNT_TYPES.BET: {
    formatType = formatConfig.betFormat;
    boShowCurrency = formatConfig.showBetCurrency;
    break;
  }
  case AMOUNT_TYPES.LIMIT: {
    formatType = formatConfig.limitFormat;
    boShowCurrency = formatConfig.showLimitCurrency;
    break;
  }
  case AMOUNT_TYPES.CHIP: {
    formatType = AMOUNT_FORMAT_TYPES.EXTEND_LETTER;
    boShowCurrency = false;
    break;
  }
  default:
    break;
  }

  // handle each format separately
  switch (formatType) {
  // on comma, separate value by commas, only 2 digits after decimal
  case AMOUNT_FORMAT_TYPES.COMMA: {
    returnValue = addAmountSeparator(addAmountToFixed(finalAmount, 2), ',');
    break;
  }
  // on entire, leave sum as is, only 2 digits after decimal
  case AMOUNT_FORMAT_TYPES.ENTIRE: {
    returnValue = addAmountToFixed(finalAmount, 2);
    break;
  }
  // on letter, if >= 1000, divide by 1000 and display k symbol, else leave amount as is, only 2 digits after decimal
  case AMOUNT_FORMAT_TYPES.LETTER: {
    if (finalAmount >= 1000) {
      returnValue = finalAmount / 1000;
      returnValue = addAmountToFixed(returnValue, 2);
      returnValue += formatConfig.kSymbol;
    } else {
      returnValue = addAmountToFixed(finalAmount, 2);
    }
    break;
  }
  // on extend letter, same as letter but also trim millions
  case AMOUNT_FORMAT_TYPES.EXTEND_LETTER: {
    if (finalAmount >= 1000000) {
      returnValue = finalAmount / 1000000;
      returnValue = addAmountToFixed(returnValue, 2);
      returnValue += formatConfig.mSymbol;
    } else if (finalAmount >= 1000) {
      returnValue = finalAmount / 1000;
      returnValue = addAmountToFixed(returnValue, 2);
      returnValue += formatConfig.kSymbol;
    } else {
      returnValue = addAmountToFixed(finalAmount, 2);
    }
    break;
  }
  default:
    break;
  }

  // Re-add negative value symbol if amount was negative
  if (isNegative) returnValue = `-${returnValue}`;

  if (!boShowCurrency) return returnValue;

  const showSymbol = typeof showCurrencySymbol !== 'undefined'
    ? showCurrencySymbol
    : formatConfig.showCurrencySymbol;
  const showCode = typeof showCurrencyCode !== 'undefined'
    ? showCurrencyCode
    : formatConfig.showCurrencyCode;

  // Add currency symbol
  if (showSymbol) {
    const symbol = getSymbolByCurrencyCode(currencyCode);
    returnValue = `${symbol} ${returnValue}`;
  }

  // Add currency code
  if (showCode) {
    returnValue = `${returnValue} ${currencyCode}`;
  }

  return returnValue;
}
